import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, Send, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import api from '../services/api';

const ForgotPassword = () => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null); // { success, message }
    const [siteLogo, setSiteLogo] = useState(null);

    useEffect(() => {
        api.get('/admin/settings/public')
            .then((res) => { if (res.data.success) setSiteLogo(res.data.data.site_logo); })
            .catch(() => {});
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setLoading(true);
        setResult(null);
        try {
            const res = await api.post('/auth/forgot-password', { email: email.trim() });
            setResult({ success: res.data.success, message: res.data.message });
        } catch (err) {
            setResult({ success: false, message: err.response?.data?.message || 'Something went wrong. Please try again.' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-hero flex items-center justify-center px-6 py-12">
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl animate-pulse-slow"></div>
                <div className="absolute bottom-20 right-10 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl animate-pulse-slow delay-200"></div>
            </div>

            <div className="relative z-10 w-full max-w-md">
                <div className="text-center mb-8">
                    <Link to="/login" className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors duration-300 mb-6">
                        <ArrowLeft className="w-5 h-5" />
                        Back to Sign In
                    </Link>

                    {siteLogo ? (
                        <div className="mx-auto mb-4">
                            <img src={siteLogo} alt="The Deep Wave" width="180" className="object-cover rounded-lg mx-auto" style={{ maxWidth: '180px', width: '180px' }} />
                        </div>
                    ) : (
                        <div className="text-6xl mb-4">🌊</div>
                    )}

                    <h1 className="text-4xl font-black gradient-text mb-2">Reset Password</h1>
                    <p className="text-gray-400">Enter your email and we'll send you a reset link</p>
                </div>

                <div className="glass-card p-8">
                    {result?.success ? (
                        <div className="text-center py-4">
                            <CheckCircle className="w-14 h-14 text-green-400 mx-auto mb-4" />
                            <h2 className="text-xl font-bold text-white mb-2">Check your inbox</h2>
                            <p className="text-gray-400 text-sm mb-6">{result.message}</p>
                            <Link to="/login" className="btn-secondary w-full flex items-center justify-center gap-2">
                                <ArrowLeft className="w-4 h-4" />
                                Back to Sign In
                            </Link>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {result && !result.success && (
                                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-center gap-3">
                                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                                    <p className="text-red-400 text-sm">{result.message}</p>
                                </div>
                            )}

                            <div>
                                <label htmlFor="email" className="block text-sm font-semibold text-gray-300 mb-2">
                                    Email Address
                                </label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="email"
                                        id="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="input-field pl-12"
                                        placeholder="your@email.com"
                                        required
                                        disabled={loading}
                                        autoFocus
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={loading || !email.trim()}
                                className="w-full btn-primary flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? (
                                    <><Loader className="w-5 h-5 animate-spin" /> Sending…</>
                                ) : (
                                    <><Send className="w-5 h-5" /> Send Reset Link</>
                                )}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;
